const DEFAULT_HELP_CENTER_URL = "https://ajuda.adatis.com.br";

/** VitePress locale path segment (root pt-BR has no prefix). */
const helpLocalePrefix = (locale?: string): string => {
  const normalized = (locale ?? "pt-BR").toLowerCase();

  if (normalized.startsWith("en")) {
    return "en";
  }

  if (normalized.startsWith("es")) {
    return "es";
  }

  return "";
};

export type HelpPersona = "owner" | "manager" | "receptionist" | "professional";

export interface HelpArticleEntry {
  path: string;
  labelKey: string;
  moduleKey: string;
  personas: HelpPersona[];
}

const normalizeBaseUrl = (url: string): string => url.replace(/\/$/, "");

/**
 * Help center host follows the SPA hostname (not a forced BR→.app flip).
 * `*.adatis.app` → help.adatis.app · otherwise REACT_APP_* / ajuda.adatis.com.br.
 */
export const getHelpCenterBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const host = window.location.hostname.toLowerCase();

    if (host === "adatis.app" || host.endsWith(".adatis.app")) {
      return "https://help.adatis.app";
    }
  }

  const fromEnv = process.env.REACT_APP_HELP_CENTER_URL;

  return normalizeBaseUrl(fromEnv || DEFAULT_HELP_CENTER_URL);
};

/**
 * Builds a help-center article URL (VitePress clean URLs; en/es use `/en/` or `/es/` prefix).
 */
export const getHelpArticleUrl = (articlePath: string, locale?: string): string => {
  const normalizedPath = articlePath.replace(/^\//, "").replace(/\/$/, "");
  const prefix = helpLocalePrefix(locale);

  if (prefix) {
    return `${getHelpCenterBaseUrl()}/${prefix}/${normalizedPath}`;
  }

  return `${getHelpCenterBaseUrl()}/${normalizedPath}`;
};

export const HELP_ARTICLE_ACCESS_LOGIN = "primeiros-passos/acesso-e-login";
export const HELP_ARTICLE_CREATE_ACCOUNT = "primeiros-passos/criar-conta";
export const HELP_ARTICLE_INITIAL_SETUP = "primeiros-passos/configuracao-inicial";
export const HELP_ARTICLE_DASHBOARD = "dashboard/visao-geral";
export const HELP_ARTICLE_AGENDA = "agenda/como-agendar";
export const HELP_ARTICLE_REVIEWS = "agenda/avaliacoes";
export const HELP_ARTICLE_ORDERS = "comandas/abrir-e-fechar";
export const HELP_ARTICLE_ORDERS_SPLIT_VOID = "comandas/pagamento-misto-e-estorno";
export const HELP_ARTICLE_CRM = "crm/oportunidades-e-orcamentos";
export const HELP_ARTICLE_CLIENTS = "clientes/cadastrar-cliente";
export const HELP_ARTICLE_CORRECT_HEALTH_RECORD = "clientes/corrigir-ficha";
export const HELP_ARTICLE_PROFESSIONALS = "equipe/cadastrar-profissional";
export const HELP_ARTICLE_SERVICE_COMMISSION = "equipe/comissao-por-servico";
export const HELP_ARTICLE_UNITS = "equipe/cadastrar-unidade";
export const HELP_ARTICLE_UNIT_SCHEDULE = "unidades/horario-do-salao";
export const HELP_ARTICLE_USERS = "equipe/gerenciar-usuarios";
export const HELP_ARTICLE_SERVICES = "catalogo/cadastrar-servico";
export const HELP_ARTICLE_SERVICE_BUNDLES = "catalogo/combos-de-servicos";
export const HELP_ARTICLE_SERVICE_PACKAGES = "catalogo/pacotes-de-sessoes";
export const HELP_ARTICLE_CLUBS = "catalogo/clubes-assinatura";
export const HELP_ARTICLE_LOYALTY = "catalogo/fidelidade-cartoes";
export const HELP_ARTICLE_PRODUCTS = "catalogo/cadastrar-produto";
export const HELP_ARTICLE_CATALOG_CATEGORIES = "catalogo/organizar-por-categoria";
export const HELP_ARTICLE_INVITE_TEAM = "equipe/convidar-equipe";
export const HELP_ARTICLE_EXPENSES = "financeiro/despesas";
export const HELP_ARTICLE_DRE = "financeiro/dre";
export const HELP_ARTICLE_SETTINGS_GENERAL = "configuracoes/geral";
export const HELP_ARTICLE_ONLINE_BOOKING = "configuracoes/agendamento-online";
export const HELP_ARTICLE_EMAIL_NOTICES = "configuracoes/avisos-por-email";
export const HELP_ARTICLE_EMAIL_CAMPAIGNS = "configuracoes/campanhas-email";
export const HELP_ARTICLE_CONTACT_RULER = "configuracoes/regua-de-contatos";
export const HELP_ARTICLE_WHATSAPP_LITE = "configuracoes/whatsapp";
export const HELP_ARTICLE_WHATSAPP_EXTENSION = "configuracoes/extensao-whatsapp";
export const HELP_ARTICLE_SETTINGS_OPERATION = "configuracoes/operacao";
export const HELP_ARTICLE_SETTINGS_PAYMENTS = "configuracoes/meios-de-pagamento";
export const HELP_ARTICLE_SETTINGS_INTEGRATIONS = "configuracoes/integracoes";
export const HELP_ARTICLE_GOOGLE_BOOKINGS = "configuracoes/google-reservas";
export const HELP_ARTICLE_META_COMMERCE = "configuracoes/catalogo-whatsapp";
export const HELP_ARTICLE_TEAM_PERMISSIONS = "configuracoes/permissoes";
export const HELP_ARTICLE_CALENDAR_EXCEPTIONS = "configuracoes/excecoes-agenda";
export const HELP_ARTICLE_ANAMNESIS = "configuracoes/fichas-de-saude";
export const HELP_ARTICLE_SUPPORT_TICKETS = "configuracoes/chamados-e-feedback";
export const HELP_ARTICLE_SUBSCRIPTION = "planos/gerenciar-assinatura";

export const ONBOARDING_HELP_ARTICLES: Partial<Record<string, string>> = {
  unit_schedule_configured: HELP_ARTICLE_UNIT_SCHEDULE,
  first_professional: HELP_ARTICLE_PROFESSIONALS,
  first_service_bundle: HELP_ARTICLE_SERVICE_BUNDLES,
  health_record_template: HELP_ARTICLE_ANAMNESIS,
};

export const HELP_ARTICLE_CATALOG: HelpArticleEntry[] = [
  {
    path: HELP_ARTICLE_ACCESS_LOGIN,
    labelKey: "help.articles.accessLogin",
    moduleKey: "help.modules.gettingStarted",
    personas: ["owner", "manager", "receptionist", "professional"],
  },
  {
    path: HELP_ARTICLE_CREATE_ACCOUNT,
    labelKey: "help.articles.createAccount",
    moduleKey: "help.modules.gettingStarted",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_INITIAL_SETUP,
    labelKey: "help.articles.initialSetup",
    moduleKey: "help.modules.gettingStarted",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_DASHBOARD,
    labelKey: "help.articles.dashboard",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_AGENDA,
    labelKey: "help.articles.agenda",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist", "professional"],
  },
  {
    path: HELP_ARTICLE_REVIEWS,
    labelKey: "help.articles.reviews",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist", "professional"],
  },
  {
    path: HELP_ARTICLE_ORDERS,
    labelKey: "help.articles.orders",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_ORDERS_SPLIT_VOID,
    labelKey: "help.articles.ordersSplitVoid",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_CRM,
    labelKey: "help.articles.crm",
    moduleKey: "help.modules.panel",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_CLIENTS,
    labelKey: "help.articles.clients",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager", "receptionist", "professional"],
  },
  {
    path: HELP_ARTICLE_CORRECT_HEALTH_RECORD,
    labelKey: "help.articles.correctHealthRecord",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager", "receptionist", "professional"],
  },
  {
    path: HELP_ARTICLE_PROFESSIONALS,
    labelKey: "help.articles.professionals",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SERVICE_COMMISSION,
    labelKey: "help.articles.serviceCommission",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_UNITS,
    labelKey: "help.articles.units",
    moduleKey: "help.modules.people",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_UNIT_SCHEDULE,
    labelKey: "help.articles.unitSchedule",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_USERS,
    labelKey: "help.articles.users",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_INVITE_TEAM,
    labelKey: "help.articles.inviteTeam",
    moduleKey: "help.modules.people",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SERVICES,
    labelKey: "help.articles.services",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SERVICE_BUNDLES,
    labelKey: "help.articles.serviceBundles",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SERVICE_PACKAGES,
    labelKey: "help.articles.servicePackages",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_CLUBS,
    labelKey: "help.articles.clubs",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_LOYALTY,
    labelKey: "help.articles.loyalty",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_PRODUCTS,
    labelKey: "help.articles.products",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_CATALOG_CATEGORIES,
    labelKey: "help.articles.catalogCategories",
    moduleKey: "help.modules.catalog",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_EXPENSES,
    labelKey: "help.articles.expenses",
    moduleKey: "help.modules.financial",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_DRE,
    labelKey: "help.articles.dre",
    moduleKey: "help.modules.financial",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SETTINGS_GENERAL,
    labelKey: "help.articles.settingsGeneral",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_ONLINE_BOOKING,
    labelKey: "help.articles.onlineBooking",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_EMAIL_NOTICES,
    labelKey: "help.articles.emailNotices",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_EMAIL_CAMPAIGNS,
    labelKey: "help.articles.emailCampaigns",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_CONTACT_RULER,
    labelKey: "help.articles.contactRuler",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_WHATSAPP_LITE,
    labelKey: "help.articles.whatsappLite",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_WHATSAPP_EXTENSION,
    labelKey: "help.articles.whatsappExtension",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager", "receptionist"],
  },
  {
    path: HELP_ARTICLE_SETTINGS_OPERATION,
    labelKey: "help.articles.settingsOperation",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_SETTINGS_PAYMENTS,
    labelKey: "help.articles.settingsPayments",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_SETTINGS_INTEGRATIONS,
    labelKey: "help.articles.settingsIntegrations",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_GOOGLE_BOOKINGS,
    labelKey: "help.articles.googleBookings",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_META_COMMERCE,
    labelKey: "help.articles.metaCommerce",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
  {
    path: HELP_ARTICLE_TEAM_PERMISSIONS,
    labelKey: "help.articles.teamPermissions",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_CALENDAR_EXCEPTIONS,
    labelKey: "help.articles.calendarExceptions",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_ANAMNESIS,
    labelKey: "help.articles.anamnesis",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SUPPORT_TICKETS,
    labelKey: "help.articles.supportTickets",
    moduleKey: "help.modules.settings",
    personas: ["owner", "manager"],
  },
  {
    path: HELP_ARTICLE_SUBSCRIPTION,
    labelKey: "help.articles.subscription",
    moduleKey: "help.modules.settings",
    personas: ["owner"],
  },
];

/**
 * Contextual help (GAP-F2-099) — maps Studio pathname → i18n topic id + help article.
 * Longer / more specific prefixes must appear before shorter ones.
 */
export interface ContextualHelpTopic {
  /** Suffix under `help.contextual.modules.{id}` (or `fallback`). */
  id: string;
  articlePath: string;
}

type ContextualHelpRoute = {
  prefix: string;
  id: string;
  articlePath: string;
};

const CONTEXTUAL_HELP_ROUTES: ContextualHelpRoute[] = [
  { prefix: "/financial/expenses", id: "expenses", articlePath: HELP_ARTICLE_EXPENSES },
  { prefix: "/financial/dre", id: "dre", articlePath: HELP_ARTICLE_DRE },
  { prefix: "/financial/day-close", id: "dayClose", articlePath: HELP_ARTICLE_DRE },
  { prefix: "/settings/general", id: "settingsGeneral", articlePath: HELP_ARTICLE_SETTINGS_GENERAL },
  { prefix: "/settings/operation", id: "settingsOperation", articlePath: HELP_ARTICLE_SETTINGS_OPERATION },
  { prefix: "/settings/payments", id: "settingsPayments", articlePath: HELP_ARTICLE_SETTINGS_PAYMENTS },
  {
    prefix: "/settings/email-notifications",
    id: "emailNotices",
    articlePath: HELP_ARTICLE_EMAIL_NOTICES,
  },
  { prefix: "/settings/messaging", id: "whatsappLite", articlePath: HELP_ARTICLE_WHATSAPP_LITE },
  {
    prefix: "/settings/whatsapp-extension",
    id: "whatsappExtension",
    articlePath: HELP_ARTICLE_WHATSAPP_EXTENSION,
  },
  {
    prefix: "/settings/team-permissions",
    id: "teamPermissions",
    articlePath: HELP_ARTICLE_TEAM_PERMISSIONS,
  },
  {
    prefix: "/settings/calendar-exceptions",
    id: "calendarExceptions",
    articlePath: HELP_ARTICLE_CALENDAR_EXCEPTIONS,
  },
  {
    prefix: "/settings/integrations",
    id: "settingsIntegrations",
    articlePath: HELP_ARTICLE_SETTINGS_INTEGRATIONS,
  },
  { prefix: "/settings/anamnesis-templates", id: "anamnesis", articlePath: HELP_ARTICLE_ANAMNESIS },
  {
    prefix: "/settings/catalog-categories",
    id: "catalogCategories",
    articlePath: HELP_ARTICLE_CATALOG_CATEGORIES,
  },
  { prefix: "/settings/subscription", id: "subscription", articlePath: HELP_ARTICLE_SUBSCRIPTION },
  { prefix: "/settings/account", id: "settingsGeneral", articlePath: HELP_ARTICLE_SETTINGS_GENERAL },
  { prefix: "/settings", id: "settingsGeneral", articlePath: HELP_ARTICLE_SETTINGS_GENERAL },
  {
    prefix: "/marketing/email-campaigns",
    id: "emailCampaigns",
    articlePath: HELP_ARTICLE_EMAIL_CAMPAIGNS,
  },
  {
    prefix: "/marketing/contact-ruler",
    id: "contactRuler",
    articlePath: HELP_ARTICLE_CONTACT_RULER,
  },
  { prefix: "/marketing", id: "contactRuler", articlePath: HELP_ARTICLE_CONTACT_RULER },
  { prefix: "/support/tickets", id: "supportTickets", articlePath: HELP_ARTICLE_SUPPORT_TICKETS },
  { prefix: "/support/chat", id: "supportTickets", articlePath: HELP_ARTICLE_SUPPORT_TICKETS },
  { prefix: "/support", id: "supportTickets", articlePath: HELP_ARTICLE_SUPPORT_TICKETS },
  { prefix: "/products/valuation", id: "products", articlePath: HELP_ARTICLE_PRODUCTS },
  { prefix: "/products", id: "products", articlePath: HELP_ARTICLE_PRODUCTS },
  { prefix: "/service-bundles", id: "serviceBundles", articlePath: HELP_ARTICLE_SERVICE_BUNDLES },
  { prefix: "/service-packages", id: "servicePackages", articlePath: HELP_ARTICLE_SERVICE_PACKAGES },
  {
    prefix: "/client-subscription-plans",
    id: "clubs",
    articlePath: HELP_ARTICLE_CLUBS,
  },
  { prefix: "/loyalty-cards", id: "loyalty", articlePath: HELP_ARTICLE_LOYALTY },
  { prefix: "/services", id: "services", articlePath: HELP_ARTICLE_SERVICES },
  { prefix: "/professionals", id: "professionals", articlePath: HELP_ARTICLE_PROFESSIONALS },
  { prefix: "/units", id: "units", articlePath: HELP_ARTICLE_UNITS },
  { prefix: "/users", id: "users", articlePath: HELP_ARTICLE_USERS },
  { prefix: "/clients", id: "clients", articlePath: HELP_ARTICLE_CLIENTS },
  { prefix: "/consulta", id: "counterLookup", articlePath: HELP_ARTICLE_CLIENTS },
  { prefix: "/orders", id: "orders", articlePath: HELP_ARTICLE_ORDERS },
  { prefix: "/crm", id: "crm", articlePath: HELP_ARTICLE_CRM },
  { prefix: "/reviews", id: "reviews", articlePath: HELP_ARTICLE_REVIEWS },
  { prefix: "/agenda", id: "agenda", articlePath: HELP_ARTICLE_AGENDA },
  { prefix: "/dashboard", id: "dashboard", articlePath: HELP_ARTICLE_DASHBOARD },
  { prefix: "/help", id: "fallback", articlePath: HELP_ARTICLE_INITIAL_SETUP },
];

export const resolveContextualHelp = (pathname: string): ContextualHelpTopic => {
  const path = (pathname.split("?")[0] || "/").replace(/\/$/, "") || "/";

  for (const route of CONTEXTUAL_HELP_ROUTES) {
    if (path === route.prefix || path.startsWith(`${route.prefix}/`)) {
      return { id: route.id, articlePath: route.articlePath };
    }
  }

  return { id: "fallback", articlePath: HELP_ARTICLE_INITIAL_SETUP };
};

export const getHelpArticlesForPersona = (persona: HelpPersona): HelpArticleEntry[] =>
  HELP_ARTICLE_CATALOG.filter((article) => article.personas.includes(persona));

export const groupHelpArticlesByModule = (
  articles: HelpArticleEntry[]
): { moduleKey: string; articles: HelpArticleEntry[] }[] => {
  const order = [
    "help.modules.gettingStarted",
    "help.modules.panel",
    "help.modules.people",
    "help.modules.catalog",
    "help.modules.financial",
    "help.modules.settings",
  ];

  const grouped = new Map<string, HelpArticleEntry[]>();

  articles.forEach((article) => {
    const list = grouped.get(article.moduleKey) ?? [];
    list.push(article);
    grouped.set(article.moduleKey, list);
  });

  return order
    .filter((moduleKey) => grouped.has(moduleKey))
    .map((moduleKey) => ({
      moduleKey,
      articles: grouped.get(moduleKey) ?? [],
    }));
};
